/**
 * Money and derived-metric helpers.
 *
 * Two rules hold everywhere in Affily:
 *
 *   1. Amounts are integer cents. Never floats. `0.1 + 0.2 !== 0.3` is not an
 *      acceptable failure mode when someone is being paid.
 *   2. Derived metrics with an empty denominator return `null`, never 0, NaN or
 *      Infinity. A partnership with no clicks yet has an *unknown* EPC, not an
 *      EPC of zero — and the UI renders that honestly as an em dash.
 */

/** Multiply an integer-cent amount by a basis-point rate, returning integer cents. */
export function applyBps(amountCents, bps) {
  assertInteger(amountCents, 'amountCents');
  assertInteger(bps, 'bps');
  // Both operands are integers, so the product is exact up to 2^53. A $1M order
  // at 10000bps is 1e8 * 1e4 = 1e12 — comfortably inside safe-integer range.
  return Math.round((amountCents * bps) / 10_000);
}

/** Basis points → percent as a number (1500 → 15). */
export function bpsToPercent(bps) {
  return bps / 100;
}

/** Percent → basis points (15 → 1500). Rounds to the nearest bp. */
export function percentToBps(percent) {
  return Math.round(percent * 100);
}

/**
 * A store's decimal money string ("49.90", "1200", "0.5") → integer cents, or
 * null when it is not a plain non-negative decimal.
 *
 * Parsed as text, never through a float: `Number("19.99") * 100` is
 * 1998.9999999999998, and rounding that back is a guess about what the store
 * meant. A third decimal place (some currencies, some tax plugins) rounds half
 * up on the digits themselves.
 */
export function decimalToCents(value) {
  const text = typeof value === 'number' ? String(value) : String(value ?? '').trim();
  const match = /^(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;
  const [, whole, fraction = ''] = match;
  const cents = Number(whole) * 100 + Number((fraction + '00').slice(0, 2));
  const total = cents + (Number(fraction[2] ?? 0) >= 5 ? 1 : 0);
  return Number.isSafeInteger(total) ? total : null;
}

/** Integer cents → a plain two-decimal string for a store API (1234 → "12.34"). */
export function centsToDecimal(cents) {
  assertInteger(cents, 'cents');
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Basis points → a percent string with two decimals (1250 → "12.50"). */
export function bpsToPercentString(bps) {
  // Basis points are hundredths of a percent: the same digit shuffle as cents.
  assertInteger(bps, 'bps');
  return centsToDecimal(bps);
}

/**
 * Earnings per click. `null` when there are no clicks to divide by.
 * Returns a float dollar amount — EPC is a display metric, never a payable.
 */
export function computeEpc(earningsCents, uniqueClicks) {
  if (!uniqueClicks || uniqueClicks <= 0) return null;
  return earningsCents / 100 / uniqueClicks;
}

/**
 * Conversion rate as a percent. `null` when there are no clicks.
 * Deliberately un-rounded at the source: bad numbers get shown, not massaged.
 */
export function computeConversionRate(conversions, uniqueClicks) {
  if (!uniqueClicks || uniqueClicks <= 0) return null;
  return (conversions / uniqueClicks) * 100;
}

/** Average order value in cents. `null` when there are no conversions. */
export function computeAov(revenueCents, conversions) {
  if (!conversions || conversions <= 0) return null;
  return Math.round(revenueCents / conversions);
}

/** Format integer cents as a currency string. */
export function formatCents(
  cents,
  { currency = 'USD', locale = 'en-US', compact = false, dropZeroCents = false } = {},
) {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    notation: compact ? 'compact' : 'standard',
    // "$480" reads better than "$480.00" for a stated retail value, but "$4.50"
    // must keep its cents — only whole-dollar amounts lose them.
    ...(dropZeroCents && cents % 100 === 0 ? { minimumFractionDigits: 0 } : {}),
    maximumFractionDigits: compact ? 1 : 2,
  }).format(cents / 100);
}

/** Format an EPC (already in dollars) — always 2dp, em dash when unknown. */
export function formatEpc(epc, { currency = 'USD', locale = 'en-US' } = {}) {
  if (epc === null || epc === undefined) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(epc);
}

/** Format a percent value. Em dash when unknown — never a fabricated 0%. */
export function formatPercent(percent, { digits = 2, locale = 'en-US' } = {}) {
  if (percent === null || percent === undefined) return '—';
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(percent)}%`;
}

/** Format a whole number with thousands separators. */
export function formatCount(n, { locale = 'en-US', compact = false } = {}) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat(locale, {
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 0,
  }).format(n);
}

function assertInteger(value, name) {
  if (!Number.isInteger(value)) {
    throw new TypeError(`${name} must be an integer (got ${value}). Money is always integer cents.`);
  }
}
